"use client";

import { useEffect, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
	clearImageryEmailCookie,
	getImagerySessionCookie,
} from "@/lib/cookies";
import { useImageryRequestsHeaderSlot } from "@/contexts/ImageryRequestsHeaderContext";

function normalizePath(p: string) {
	return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

/**
 * Keeps the imagery header email / actions in sync with the imagery session cookie on
 * routes other than `/imagery/requests` (that page registers the slot itself, including
 * `onDifferentEmail` that resets local list state). No unmount cleanup that clears the
 * slot—requests page owns cleanup when leaving that flow.
 */
export function ImageryCookieHeaderSlotSync() {
	const router = useRouter();
	const pathname = usePathname() ?? "";
	const normalized = normalizePath(pathname);
	const { setImageryRequestsHeaderSlot } = useImageryRequestsHeaderSlot();

	const onDifferentEmail = useCallback(() => {
		clearImageryEmailCookie();
		setImageryRequestsHeaderSlot(null);
		router.refresh();
	}, [router, setImageryRequestsHeaderSlot]);

	const onNewRequest = useCallback(() => {
		router.push("/imagery/request/new");
	}, [router]);

	useEffect(() => {
		if (normalized === "/imagery/requests") {
			return;
		}
		const s = getImagerySessionCookie();
		if (!s?.email || !s.enrichmentPublicEntityId) {
			setImageryRequestsHeaderSlot(null);
			return;
		}
		setImageryRequestsHeaderSlot({
			email: s.email,
			onDifferentEmail,
			onNewRequest,
		});
	}, [
		normalized,
		onDifferentEmail,
		onNewRequest,
		setImageryRequestsHeaderSlot,
	]);

	return null;
}
